import {flow, get, isEmpty, negate} from 'lodash/fp';
import {createSelector} from 'reselect';

import {sameDayLastMonthById} from '/redux/selector/coach.selector';
import {percentDifference} from '/util/number.util';
import {fromProps} from '/util/selector.util';

import {metric as metricState} from './root.selector';

export const homeMetrics = createSelector(metricState, get('homeMetrics'));

export const homeMetricsById = createSelector(
  fromProps(get('coachId')),
  homeMetrics,
  (coachId, metrics) => {
    return get(coachId)(metrics);
  },
);

export const hasHomeMetricsById = createSelector(
  homeMetricsById,
  negate(isEmpty),
);

// Frontline Clients With Expiring Rewards
export const frontlineClientsWithExpiringRewards = createSelector(
  homeMetricsById,
  get('frontlineClientsWithExpiringRewardsCount'),
);

export const allOrgClientsWithExpiringRewards = createSelector(
  homeMetricsById,
  get('allOrgClientsWithExpiringRewardsCount'),
);

export const allOrgClientsWithExpiringWellnessCredits = createSelector(
  homeMetricsById,
  get('allOrgClientsWithExpiringWellnessCreditsCount'),
);

export const frontlineClientsWithExpiringWellnessCredits = createSelector(
  homeMetricsById,
  get('frontlineClientsWithExpiringWellnessCreditsCount'),
);

// Clients
export const homeMetricsClients = createSelector(
  homeMetricsById,
  get('clients'),
);

export const newFrontlineClientsCount = createSelector(
  homeMetricsClients,
  flow([get('newFrontlineClientsCount'), parseInt]),
);

export const frontlineClientsCount = createSelector(
  homeMetricsClients,
  flow([get('frontlineClientsCount'), parseInt]),
);

export const newGen0ClientsCount = createSelector(
  homeMetricsClients,
  flow([get('newGen0ClientsCount'), parseInt]),
);

export const gen0ClientsCount = createSelector(
  homeMetricsClients,
  get('gen0ClientsCount'),
);

export const newOrganizationClientsCount = createSelector(
  homeMetricsClients,
  flow([get('newOrganizationClientsCount'), parseInt]),
);

export const organizationClientsCount = createSelector(
  homeMetricsClients,
  get('organizationClientsCount'),
);

export const inactiveClientsCount = createSelector(
  homeMetricsClients,
  flow([get('inactiveClientsCount'), parseInt]),
);

// Coaches
export const homeMetricsCoaches = createSelector(
  homeMetricsById,
  get('coaches'),
);

export const newFrontlineCoachesCount = createSelector(
  homeMetricsCoaches,
  flow([get('newFrontlineCoachesCount'), parseInt]),
);

export const frontlineCoachesCount = createSelector(
  homeMetricsCoaches,
  flow([get('frontlineCoachesCount'), parseInt]),
);

export const newOrganizationCoachesCount = createSelector(
  homeMetricsCoaches,
  flow([get('newOrganizationCoachesCount'), parseInt]),
);

export const organizationCoachesCount = createSelector(
  homeMetricsCoaches,
  get('organizationCoachesCount'),
);
export const myTeamsCount = createSelector(
  homeMetricsCoaches,
  flow([get('myTeamsCount'), parseInt]),
);

export const viewTeamsCount = createSelector(
  homeMetricsCoaches,
  flow([get('viewTeamsCount'), parseInt]),
);
export const newGen0CoachesCount = createSelector(
  homeMetricsCoaches,
  flow([get('newGen0CoachesCount'), parseInt]),
);

export const gen0CoachesCount = createSelector(
  homeMetricsCoaches,
  get('gen0CoachesCount'),
);

// Teams
export const homeMetricsTeams = createSelector(homeMetricsById, get('teams'));

export const seniorCoachTeamsCount = createSelector(
  homeMetricsTeams,
  flow([get('seniorCoachTeamsCount'), parseInt]),
);

export const executiveDirectorTeamsCount = createSelector(
  homeMetricsTeams,
  flow([get('executiveDirectorTeamsCount'), parseInt]),
);

export const fibcTeamsCount = createSelector(
  homeMetricsTeams,
  flow([get('fibcTeamsCount'), parseInt]),
);

export const fiblTeamsCount = createSelector(
  homeMetricsTeams,
  flow([get('fiblTeamsCount'), parseInt]),
);

// FRONTLINE COACH VALUES
export const frontlineQualifyingVolume = createSelector(
  homeMetricsById,
  get('frontlineQualifyingVolume'),
);

export const frontlineQualifyingVolumeCurrent = createSelector(
  homeMetricsById,
  get('frontlineQualifyingVolume.current'),
);

export const frontlineQualifyingVolumeProjectedThisMonth = createSelector(
  homeMetricsById,
  get('frontlineQualifyingVolume.projectedThisMonth'),
);

export const frontlineQualifyingVolumeSameDayLastMonth = createSelector(
  sameDayLastMonthById,
  get('frontlineVol'),
);

export const frontlineQualifyingVolumeTotalLastMonth = createSelector(
  homeMetricsById,
  get('frontlineQualifyingVolume.totalLastMonth'),
);

export const frontlineQualifyingVolumeDelta = createSelector(
  frontlineQualifyingVolumeCurrent,
  frontlineQualifyingVolumeSameDayLastMonth,
  percentDifference,
);

export const frontLineCoachMetrics = createSelector(
  frontlineQualifyingVolumeCurrent,
  frontlineQualifyingVolumeProjectedThisMonth,
  frontlineQualifyingVolumeSameDayLastMonth,
  frontlineQualifyingVolumeTotalLastMonth,
  frontlineQualifyingVolumeDelta,
  (current, projectedThisMonth, thisDayLastMonth, totalLastMonth, delta) => {
    return {
      current,
      projectedThisMonth,
      thisDayLastMonth: thisDayLastMonth,
      totalLastMonth,
      delta: delta,
    };
  },
);

// GEN 0 COACH VALUES
export const gen0Volume = createSelector(homeMetricsById, get('gen0Volume'));

export const gen0VolumeCurrent = createSelector(
  homeMetricsById,
  get('gen0Volume.current'),
);

export const gen0VolumeProjectedThisMonth = createSelector(
  homeMetricsById,
  get('gen0Volume.projectedThisMonth'),
);

export const gen0VolumeSameDayLastMonth = createSelector(
  sameDayLastMonthById,
  get('gen0Vol'),
);

export const gen0VolumeTotalLastMonth = createSelector(
  homeMetricsById,
  get('gen0Volume.totalLastMonth'),
);

export const gen0VolumeDelta = createSelector(
  gen0VolumeCurrent,
  gen0VolumeSameDayLastMonth,
  percentDifference,
);

export const gen0CoachMetrics = createSelector(
  gen0VolumeCurrent,
  gen0VolumeProjectedThisMonth,
  gen0VolumeSameDayLastMonth,
  gen0VolumeTotalLastMonth,
  gen0VolumeDelta,
  (current, projectedThisMonth, thisDayLastMonth, totalLastMonth, delta) => {
    return {
      current,
      projectedThisMonth,
      thisDayLastMonth: thisDayLastMonth,
      totalLastMonth,
      delta: delta,
    };
  },
);

// TOTAL GENERATION COACH VALUES
export const totalGenerationVolume = createSelector(
  homeMetricsById,
  get('totalGenerationVolume'),
);

export const totalGenerationVolumeCurrent = createSelector(
  homeMetricsById,
  get('totalGenerationVolume.current'),
);

export const totalGenerationVolumeProjectedThisMonth = createSelector(
  homeMetricsById,
  get('totalGenerationVolume.projectedThisMonth'),
);

export const totalGenerationVolumeSameDayLastMonth = createSelector(
  sameDayLastMonthById,
  get('totalVol'),
);

export const totalGenerationVolumeTotalLastMonth = createSelector(
  homeMetricsById,
  get('totalGenerationVolume.totalLastMonth'),
);

export const totalGenerationVolumeDelta = createSelector(
  totalGenerationVolumeCurrent,
  totalGenerationVolumeSameDayLastMonth,
  percentDifference,
);

export const totalGenerationVolumeMetrics = createSelector(
  totalGenerationVolumeCurrent,
  totalGenerationVolumeProjectedThisMonth,
  totalGenerationVolumeSameDayLastMonth,
  totalGenerationVolumeTotalLastMonth,
  totalGenerationVolumeDelta,
  (current, projectedThisMonth, thisDayLastMonth, totalLastMonth, delta) => {
    return {
      current,
      projectedThisMonth,
      thisDayLastMonth: thisDayLastMonth,
      totalLastMonth,
      delta: delta,
    };
  },
);
